import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { switchMap, take } from 'rxjs/operators';

import { AuthService, User } from '@core';
import { Endpoints } from '@core/interfaces/Endpoints';
import { TMetadata } from '@core/interfaces/TMetadata';

import { isSlugCreatedInMetadata } from '@shared/utils/slug';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private _http: HttpClient, private _authService: AuthService) {}

  public updateCurrentUser(data: Partial<User>, callback: () => void): void {
    this._authService.user().pipe(take(1)).subscribe(currentUser => {
      if (!currentUser?.name) return;

      this._http.put(Endpoints.user.updateUserByUsername.url(currentUser.name), data).subscribe({
        error: err => {
          console.error(err);
        },
        next: () => {
          this._authService.setUser({ ...currentUser, ...data });
          callback();
        },
      });
    });
  }

  public updateCurrentUserMetadata(data: TMetadata, callback: () => void): void {
    const isDisplayNameModified = data.slug === 'display_name';

    this._authService.user().pipe(take(1)).subscribe(currentUser => {
      if (!currentUser?.name) return;

      let body: TMetadata[];
      if (currentUser.metadata && !isSlugCreatedInMetadata(data.slug, currentUser.metadata)) {
        body = [...currentUser.metadata, data];
      } else {
        body = [data];
      }

      this._http.put(Endpoints.user.updateUserByUsername.url(currentUser.name), { metadata: body }).subscribe({
        error: err => {
          console.error(err);
        },
        next: () => {
          const userFields: User = {
            ...currentUser,
            metadata: body,
          };
          if (isDisplayNameModified) {
            userFields.displayName = data.value as string;
          }
          this._authService.setUser(userFields);
          callback();
        },
      });
    });
  }

  updateCurrentUserPassword(oldPassword: string, newPassword: string, callback: () => void, errorCallback: () => void) {
    this._authService.user().pipe(
      take(1),
      switchMap(currentUser => {
        if (!currentUser?.name) {
          throw new Error('No authenticated user');
        }
        return this._http.put(Endpoints.user.changePassword.url(currentUser.name), {
          oldPassword,
          newPassword,
        });
      }),
    ).subscribe({
      error: () => {
        errorCallback();
      },
      next: () => {
        callback();
      },
    });
  }
}
